import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Gift, Link as LinkIcon, Users, TrendUp } from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border border-border rounded-md px-3 py-2 shadow-md text-xs">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          {p.name}: <span className="font-semibold">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

export default function ReferralProgrammePage() {
  const { user } = useAuth();
  const isPlatformAdmin = user?.is_platform_admin;
  const [myCode, setMyCode] = useState(null);
  const [history, setHistory] = useState(null);
  const [adminStats, setAdminStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [codeRes, histRes] = await Promise.all([
        API.get("/referrals/my-code"),
        API.get("/referrals/history"),
      ]);
      setMyCode(codeRes.data);
      setHistory(histRes.data);

      if (isPlatformAdmin) {
        const { data } = await API.get("/analytics/referrals");
        setAdminStats(data);
      }
    } catch {
      toast.error("Failed to load referral data");
    } finally {
      setLoading(false);
    }
  }, [isPlatformAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading referral programme...</div>;


  return (
    <div data-testid="referral-programme-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
          <Gift size={18} className="text-white" weight="fill" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Referral Programme</h1>
          <p className="text-xs text-muted-foreground">Retired — replaced by the Fixlr Resellers programme</p>
        </div>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 px-4 py-3 text-xs text-amber-800 dark:text-amber-200 mb-6" data-testid="referral-retired-banner">
        The free-days referral programme has closed. Days already earned stay on your account. Fixlr now works with resellers who give new customers an ongoing discount code — if you'd like to become a partner, contact the Fixlr team.
      </div>

      {/* My Referral Code Section */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="border border-border rounded-lg p-5 bg-card" data-testid="my-referral-code">
          <div className="flex items-center gap-2 mb-3">
            <LinkIcon size={18} className="text-amber-500" weight="duotone" />
            <h3 className="text-sm font-semibold font-[Chivo]">Your Referral Code</h3>
          </div>
          {myCode?.code ? (
            <>
              <div className="flex items-center gap-2 mb-3">
                <div className="flex-1 bg-muted rounded-md px-4 py-3 text-center">
                  <span className="text-lg font-bold font-mono tracking-widest line-through text-muted-foreground" data-testid="referral-code-value">{myCode.code}</span>
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground mb-2">This code no longer grants free days.</p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No referral code — the programme has been retired</p>
          )}
        </div>

        <div className="border border-border rounded-lg p-5 bg-card" data-testid="referral-rewards">
          <div className="flex items-center gap-2 mb-3">
            <TrendUp size={18} className="text-emerald-500" weight="duotone" />
            <h3 className="text-sm font-semibold font-[Chivo]">Your Rewards</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-muted/50 rounded-md p-3 text-center">
              <p className="text-2xl font-bold font-[Chivo] text-emerald-600" data-testid="successful-referrals">{myCode?.successful_referrals || 0}</p>
              <p className="text-[10px] text-muted-foreground">Successful Referrals</p>
            </div>
            <div className="bg-muted/50 rounded-md p-3 text-center">
              <p className="text-2xl font-bold font-[Chivo] text-amber-600" data-testid="total-reward-days">{history?.total_rewards_days || 0}</p>
              <p className="text-[10px] text-muted-foreground">Bonus Days Earned</p>
            </div>
          </div>
          <div className="mt-3 p-2 rounded bg-primary/5 border border-primary/10">
            <p className="text-[11px] text-muted-foreground">
              <strong className="text-foreground">Programme closed:</strong> bonus days already earned remain on your subscription; no new rewards are issued.
            </p>
          </div>
        </div>
      </div>

      {/* Referral History */}
      {history?.referrals?.length > 0 && (
        <div className="border border-border rounded-lg overflow-hidden mb-6" data-testid="referral-history">
          <div className="px-4 py-3 bg-muted/30 border-b border-border">
            <h3 className="text-sm font-semibold font-[Chivo]">Referral History</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-muted/10">
                  <th className="px-3 py-2 text-left font-semibold">Referred Tenant</th>
                  <th className="px-3 py-2 text-center font-semibold">Status</th>
                  <th className="px-3 py-2 text-right font-semibold">Reward</th>
                  <th className="px-3 py-2 text-right font-semibold">Date</th>
                </tr>
              </thead>
              <tbody>
                {history.referrals.map((r, i) => (
                  <tr key={r.id || i} className="border-b border-border hover:bg-muted/10">
                    <td className="px-3 py-2 font-medium">{r.referred_tenant_id?.slice(0, 8)}...</td>
                    <td className="px-3 py-2 text-center">
                      <Badge className={r.status === "converted" ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}>
                        {r.status === "converted" ? "Converted" : r.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right text-emerald-600 font-medium">+{r.reward_days} days</td>
                    <td className="px-3 py-2 text-right text-muted-foreground">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Platform Admin: Referral Analytics */}
      {isPlatformAdmin && adminStats && (
        <div className="border border-border rounded-lg p-5 bg-card" data-testid="admin-referral-analytics">
          <div className="flex items-center gap-2 mb-4">
            <Users size={18} className="text-violet-500" weight="duotone" />
            <h3 className="text-sm font-semibold font-[Chivo]">Platform Referral Analytics</h3>
            <Badge variant="outline" className="text-[9px] ml-auto">Admin</Badge>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-muted/50 rounded-md p-3 text-center">
              <p className="text-xl font-bold font-[Chivo]" data-testid="admin-total-codes">{adminStats.total_referral_codes}</p>
              <p className="text-[10px] text-muted-foreground">Total Codes Created</p>
            </div>
            <div className="bg-muted/50 rounded-md p-3 text-center">
              <p className="text-xl font-bold font-[Chivo] text-emerald-600" data-testid="admin-total-referrals">{adminStats.total_successful_referrals}</p>
              <p className="text-[10px] text-muted-foreground">Successful Referrals</p>
            </div>
            <div className="bg-muted/50 rounded-md p-3 text-center">
              <p className="text-xl font-bold font-[Chivo] text-amber-600" data-testid="admin-total-reward-days">{adminStats.total_reward_days_given}</p>
              <p className="text-[10px] text-muted-foreground">Total Reward Days Given</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
